import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, Shield, Smartphone } from 'lucide-react';
import { collectFingerprint, getStoredDeviceId } from '../utils/deviceFingerprint';
import axios from 'axios';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, complete2FALogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const codeInputRef = useRef(null);
  
  // Device Fingerprint State
  const [fingerprint, setFingerprint] = useState(null);
  const deviceId = getStoredDeviceId();

  const from = location.state?.from || '/';

  // Collect fingerprint on mount
  useEffect(() => {
    const collectFP = async () => {
      try {
        const fp = await collectFingerprint();
        setFingerprint(fp);
      } catch (error) {
        console.warn('Failed to collect fingerprint:', error);
      }
    };
    collectFP();
  }, []);

  useEffect(() => {
    if (show2FAModal && codeInputRef.current) {
      setTimeout(() => codeInputRef.current?.focus(), 100);
    }
  }, [show2FAModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Compila tutti i campi');
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password, deviceId);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setTempToken(result.temp_token);
        setShow2FAModal(true);
        setLoading(false);
        return;
      }
      
      // Normal login (no 2FA)
      toast.success('Accesso effettuato!');
      navigateAfterLogin(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (verificationCode.length < 6) {
      toast.error('Inserisci un codice di 6 cifre');
      return;
    }

    setVerifying(true);
    try {
      const user = await complete2FALogin(tempToken, verificationCode, trustDevice, deviceId);
      toast.success('Accesso effettuato con 2FA!');
      setShow2FAModal(false);
      navigateAfterLogin(user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setVerifying(false);
    }
  };

  const navigateAfterLogin = async (user) => {
    // Send fingerprint after successful login
    if (fingerprint) {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/device/fingerprint', {
          fingerprint,
          action: 'login'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.warn('Failed to send fingerprint:', error);
      }
    }
    
    if (user.user_type === 'admin') {
      navigate('/admin');
    } else if (user.user_type === 'agent') {
      navigate('/agent');
    } else if (user.user_type === 'seller') {
      navigate('/seller');
    } else {
      navigate(from === '/login' ? '/buyer' : from);
    }
  };

  const handleGoogleLogin = () => {
    // Use origin without hardcoded path - AuthCallback handles routing based on user type
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    
    // Auto-submit when 6 digits entered
    if (value.length === 6) {
      setTimeout(() => handle2FAVerify(), 100);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center py-16 px-6">
        <div className="w-full max-w-md">
          {/* Main Card */}
          <div className="premium-card p-10">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="text-3xl font-semibold tracking-tight text-[#1D1D1F] mb-3">
                Bentornato
              </h1>
              <p className="text-[15px] text-[#86868B]">
                Accedi al tuo account WEQASA
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-[14px] font-medium text-[#1D1D1F]">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#86868B]" strokeWidth={1.5} />
                  <input
                    id="email"
                    type="email"
                    placeholder="nome@esempio.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-apple pl-11"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-[14px] font-medium text-[#1D1D1F]">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#86868B]" strokeWidth={1.5} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-apple pl-11 pr-11"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#86868B] hover:text-[#1D1D1F] transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="text-right">
                <Link to="/password-reset" className="text-[14px] text-[#0A2540] hover:text-[#061A2D] font-medium transition-colors">
                  Password dimenticata?
                </Link>
              </div>

              {/* Submit Button */}
              <button 
                type="submit" 
                className="w-full btn-apple-primary justify-center"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Accesso in corso...
                  </>
                ) : (
                  <>
                    Accedi
                    <ArrowRight className="ml-2 h-5 w-5" strokeWidth={1.5} />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-black/5" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-white text-[13px] text-[#86868B]">oppure</span>
              </div>
            </div>

            {/* Google Login */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full btn-apple-outline justify-center"
              data-testid="google-login-btn"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continua con Google
            </button>

            {/* Register Links */}
            <div className="mt-8 text-center text-[14px] text-[#86868B]">
              Sei un agente immobiliare o un costruttore?{' '}
              <Link to="/registrati/agente" className="text-[#0071e3] font-medium hover:underline transition-colors">
                Clicca qui
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 2FA Verification Modal */}
      <Dialog open={show2FAModal} onOpenChange={setShow2FAModal}>
        <DialogContent className="sm:max-w-md" data-testid="2fa-login-modal">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center">
                <Shield className="h-8 w-8 text-[#0071e3]" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Verifica in due passaggi</DialogTitle>
            <DialogDescription className="text-center">
              Inserisci il codice a 6 cifre dalla tua app di autenticazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* Code Input */}
            <div className="flex justify-center">
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="000000"
                className="w-48 h-14 text-center text-3xl tracking-[0.5em] font-mono rounded-xl border border-[#E5E5EA] focus:border-[#0071e3] focus:ring-2 focus:ring-[#0071e3]/20 outline-none transition-all"
                maxLength={6}
                autoComplete="one-time-code"
                data-testid="2fa-code-input"
              />
            </div>

            {/* Smartphone hint */}
            <div className="flex items-center justify-center text-sm text-[#86868B]">
              <Smartphone className="h-4 w-4 mr-2" />
              <span>Apri Google Authenticator o Authy</span>
            </div>

            {/* Trust Device Checkbox */}
            <div className="flex items-center justify-center space-x-2">
              <Checkbox
                id="trust-device"
                checked={trustDevice}
                onCheckedChange={setTrustDevice}
              />
              <Label htmlFor="trust-device" className="text-sm text-[#86868B] cursor-pointer">
                Non chiedere più su questo dispositivo
              </Label>
            </div>

            {/* Verify Button */}
            <Button
              onClick={handle2FAVerify}
              disabled={verificationCode.length !== 6 || verifying}
              className="w-full h-12 rounded-full bg-[#0071e3] hover:bg-[#0077ED]"
              data-testid="2fa-verify-btn"
            >
              {verifying ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Verifica in corso...
                </>
              ) : (
                'Verifica'
              )}
            </Button>

            {/* Backup code hint */}
            <p className="text-xs text-center text-[#86868B]">
              Hai perso l'accesso all'app? Usa un codice di backup.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default LoginPage;
